import { QueryClient } from 'react-query';
import { get } from './utils';

/**
 * The org-scale list contract (api.tetherme.io Phase 2).
 *
 * Endpoints that adopted it answer one of two shapes for a list request:
 *
 *   - the legacy bare array, when the API's ORG_SCALE_PAGINATION flag is OFF, or when the
 *     caller sent no paging parameter at all;
 *   - a {@link Page} envelope, when the flag is on and the caller did send one.
 *
 * A third state matters just as much: with the per-organisation ENFORCE_PAGINATION flag
 * on, an unpaged list request is answered with 400. That is the state these helpers exist
 * for — a screen that never sends paging parameters does not degrade under enforcement,
 * it breaks.
 */
export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  /**
   * Did the SERVER page this, or did {@link getPage} window a legacy array?
   *
   * Set by getPage, never by the API. It matters wherever the two states differ in more
   * than speed — the organisation-property-info endpoint drops its device counts to zero
   * on the unpaged branch past 100 properties, so a table rendering that column has to
   * know which branch answered it.
   */
  serverPaged?: boolean;
  /**
   * How many rows the SERVER answered with, before any client-side search narrowed them.
   *
   * Only set on the fallback branch, where it is the size of the whole list. `total` is
   * the number of MATCHES, which is the right number for a footer and the wrong one for
   * any question about the collection itself: the API drops device counts past 100
   * PROPERTIES, and comparing a 30-match search against that cutoff read as "under the
   * cutoff, so the zeroes are real" for an organisation with 1,500 of them.
   */
  unfilteredTotal?: number;
}

/**
 * The server's ceiling on `limit`. Asking for more is a 400, not a clamp, so callers that
 * page through a whole list have to walk in steps of at most this.
 */
export const MAX_PAGE_LIMIT = 200;

export function isPage<T>(response: T[] | Page<T> | undefined): response is Page<T> {
  return Boolean(response) && !Array.isArray(response) && Array.isArray((response as Page<T>)?.items);
}

/** Paging/search/sort parameters for a list request. */
export interface PageRequest {
  limit: number;
  offset: number;
  search?: string;
  sort?: string;
  desc?: boolean;
}

/**
 * What to do with a search or a sort when the SERVER did not do it.
 *
 * The flag-off response is the legacy bare array, and the legacy handlers behind these
 * endpoints do not know the `search` and `sort` parameters at all — `searchProperties`
 * takes an organisationId and nothing else; `userorganisation`'s legacy branch is a
 * `findAll` on a where clause of ids. Windowing that array into the search-result slot
 * hands the user the organisation's first 20 rows as though they matched what they typed,
 * with a footer counting the whole organisation. Not an error, not an empty state: a
 * confident wrong answer, on today's default flag state.
 *
 * So a caller that can search MUST say how to match a row, and the filtering happens here
 * instead. It is the same client-side filtering these screens did before they were paged —
 * correct, because with the flag off the response really is the whole list.
 */
export interface PageFallback<T> {
  /** The strings a free-text `search` matches, for a row. Case-insensitive, substring. */
  searchable?: (item: T) => (string | undefined | null)[];
  /**
   * The value a `sort` field orders by, for a row.
   *
   * Required of any caller that lets the user sort, for the same reason `searchable` is
   * required of one that lets them search: with the flag off the server returns the array
   * in whatever order MySQL produced it, so a table that paints a sort indicator over it
   * is asserting an order that did not happen — and across pages it is worse than
   * cosmetic, because an unordered array windowed by offset can repeat and skip rows.
   * Returning `undefined` for a field says "cannot order by this", which is a throw, not
   * a silent no-op.
   */
  sortValue?: (item: T, field: string) => string | number | undefined;
}

/**
 * A {@link PageFallback.sortValue} built from an explicit field map.
 *
 * A field the map does not name THROWS rather than returning undefined, because the two
 * are different answers: undefined is "this row has no value for that field" (sorts last,
 * which is right for a null address), while an unnamed field is "nobody taught this caller
 * how to order by that", and silently returning the rows in arbitrary order under a sort
 * indicator is the failure this whole mechanism exists to prevent.
 */
export function sortValueFrom<T>(
  fields: Record<string, (item: T) => string | number | undefined>
): (item: T, field: string) => string | number | undefined {
  return (item, field) => {
    const read = fields[field];
    if (!read) {
      throw new Error(
        `no fallback ordering for sort field "${field}" (ORG_SCALE_PAGINATION off, so the ` +
        `server did not sort); known fields: ${Object.keys(fields).join(', ')}`
      );
    }
    return read(item);
  };
}

/** Case-folded, so the comparator matches the columns' case-insensitive collation. */
function foldForSort(value: string | number | undefined): string | number | undefined {
  return typeof value === 'string' ? value.toLowerCase() : value;
}

/** The row's id, for the tiebreaker the server's ORDER BY carries. */
function idOf(row: unknown): string {
  return String((row as { id?: string })?.id ?? '');
}

function matchesSearch<T>(item: T, search: string, fallback: PageFallback<T>): boolean {
  const needle = search.trim().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  return (fallback.searchable?.(item) ?? []).some((value) =>
    typeof value === 'string' &&
    value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().includes(needle)
  );
}

/**
 * ONE page of a list endpoint.
 *
 * A table shows a page, so it should ask for a page. Walking every page moves the whole
 * table into the browser and leaves searching and sorting there too, which is the cost
 * pagination exists to remove.
 *
 * A bare array back means the ORG_SCALE_PAGINATION flag is off and the server ignored the
 * paging parameters — that array IS the whole list, so it is searched, sorted and windowed
 * here (see {@link PageFallback}) to keep the screen honest while the flag is off.
 */
/**
 * The in-flight whole-list request per endpoint+filters, so two tables mounting together
 * share one download rather than racing.
 *
 * Deliberately holds ONLY a pending promise, and deletes the entry the moment it settles:
 * nothing is cached here. The cached copy lives in the react-query cache under the key
 * below, which is what `queryClient.clear()` wipes on sign-out — a plain module-level
 * cache would survive it.
 */
const inFlightLists = new Map<string, Promise<unknown>>();

/** The signed-in session generation that owns shared whole-list requests. */
let sharedListSessionGeneration = 0;

/** The cache-key segment that marks a shared flag-off whole list. */
export const WHOLE_LIST_FALLBACK = 'wholeListFallback';

/**
 * Drop every in-flight share. Called on SIGN-OUT, beside `queryClient.clear()`.
 *
 * The cached copies go with the cache; these do not, and a whole-list request still in
 * flight when the session ends would otherwise be handed to the next caller — who is the
 * next OPERATOR, awaiting a response fetched under the previous one's token.
 */
export function clearSharedListState(): void {
  sharedListSessionGeneration += 1;
  inFlightLists.clear();
}

/**
 * Fetch a list, sharing the answer when the server gave us the WHOLE list.
 *
 * While ORG_SCALE_PAGINATION is off the server ignores the paging parameters entirely and
 * answers with the whole table — so every page, every sort and every settled keystroke is
 * a DIFFERENT react-query key over the SAME array, and each one re-downloads it. On the
 * customer list that is 107,677 rows per interaction; typing a five-character search was
 * five full downloads of a table the screen it replaced downloaded once.
 *
 * Shared through the QUERY CACHE rather than a module variable, so it has the same
 * lifetime as everything else: cleared on sign-out, dropped when a mutation invalidates it,
 * and garbage-collected once nothing references it.
 *
 * "Dropped when a mutation invalidates it" was a CLAIM, not a mechanism: the key began with
 * `WHOLE_LIST_FALLBACK`, nothing anywhere invalidated that prefix, and `CustomerListTable`'s
 * post-merge `invalidateQueries('CustomerPage')` therefore missed it entirely — the list
 * refetched and was answered from the pre-merge copy, so a merged-away customer stayed on
 * screen for the whole cacheTime window. The key now begins with the CALLER'S OWN base query
 * key, so react-query's prefix matching makes every invalidation that already targets the
 * list reach this entry too, including ones nobody has written yet. Wiring each mutation to
 * a second key by hand is the fix-one-call-site trap this branch keeps falling into.
 *
 * No-op when the server DID page (`serverPaged`) — there is no whole list to share, and
 * caching one page's rows under an endpoint-wide key would serve them to every other page.
 */
export async function fetchListSharingFallback<T>(
  queryClient: QueryClient,
  key: unknown[],
  fetchIt: () => Promise<T[] | Page<T>>
): Promise<T[] | Page<T>> {
  const sessionGeneration = sharedListSessionGeneration;
  const endpoint = JSON.stringify(key);

  // `isInvalidated` matters: a mutation that invalidates this endpoint must not be answered
  // from the list it just invalidated.
  const state = queryClient.getQueryState<T[]>(key);
  if (state?.data && !state.isInvalidated) {
    return state.data;
  }

  const pending = inFlightLists.get(endpoint) as Promise<T[] | Page<T>> | undefined;
  if (pending) {
    const shared = await pending;

    // ONLY A WHOLE LIST IS SHAREABLE. The key deliberately excludes limit/offset/sort/search
    // because the flag-OFF server ignores them and every such request answers with the same
    // array — but whether the server ignored them is not known until a response arrives, and
    // this dedupe runs before that. The docblock above says "no-op when the server DID page";
    // the code did not implement it, so on the SERVER-PAGED branch two requests differing
    // only in `offset` — clicking next twice, or a settled keystroke landing while a page is
    // in flight — shared one promise and the second caller was handed the FIRST caller's
    // window. react-query then cached those rows under the second request's key, so page 3
    // showed page 2's rows and went on doing so.
    //
    // A Page is somebody else's window; take our own. An array is the whole table, which is
    // the same answer for every caller, which is the entire point of sharing it.
    if (Array.isArray(shared)) {
      return shared;
    }
  }

  // REGISTERED BEFORE THE AWAIT, so an invalidation landing mid-flight has something to
  // land ON. Until this entry exists a mutation's `invalidateQueries`/`resetQueries` for
  // this prefix matches nothing — create a customer while the initial list download is
  // running and the pre-mutation array was written afterwards as fresh, so the new customer
  // was missing from the list it was just added to. `web-portal`'s copy of this helper
  // registers the same way.
  const registered = queryClient.getQueryCache().build(queryClient, { queryKey: key });

  const request = fetchIt();

  // Not while another is registered: falling through from a paged share above must not
  // evict the in-flight entry a concurrent flag-off caller is waiting on.
  const shouldRegister = !inFlightLists.has(endpoint);
  if (shouldRegister) {
    inFlightLists.set(endpoint, request);
  }

  try {
    const response = await request;
    // Three things have to still be true to cache this.
    //
    // Session: an old-token response settling after sign-out must not rebuild the cache
    // that `queryClient.clear()` wiped.
    //
    // Same entry: `resetQueries` REMOVES the registration rather than flagging it, so a
    // surviving hash is not enough — it has to be the object we registered.
    //
    // Not invalidated: `invalidateQueries` marks it in place, and a response that left
    // before the mutation cannot answer for the state after it.
    const stillOurs = queryClient.getQueryCache().get(registered.queryHash) === registered;
    if (
      Array.isArray(response)
      && sessionGeneration === sharedListSessionGeneration
      && stillOurs
      && !registered.state.isInvalidated
    ) {
      queryClient.setQueryData(key, response);
    }
    return response;
  } finally {
    // Always — a rejected request must not be replayed to every later caller.
    //
    // Only if the entry is still OURS: `clearSharedListState()` empties the map on sign-out,
    // and a request issued under the old session settling afterwards would otherwise delete
    // the new session's registration and leave a concurrent caller to fetch its own copy.
    if (shouldRegister && inFlightLists.get(endpoint) === request) {
      inFlightLists.delete(endpoint);
    }
  }
}

export async function getPage<T>(
  path: string,
  request: PageRequest,
  filters: Record<string, unknown> = {},
  fallback: PageFallback<T> = {},
  /**
   * Pass it from the calling hook (`useQueryClient()`) to share the FLAG-OFF whole-list
   * download across pages, sorts and keystrokes — see {@link fetchListSharingFallback}.
   * Optional so a non-hook caller still works; without it every interaction re-downloads
   * the table while the flag is off.
   */
  queryClient?: QueryClient,
  /**
   * The caller's OWN base query key, e.g. `['CustomerPage']`.
   *
   * The shared entry is stored under this prefix so that whatever already invalidates the
   * caller's list also invalidates the shared copy — react-query matches keys by prefix.
   * Omit it and the entry is invalidated by nothing, which is how a merged-away customer
   * stayed on screen after the list had refetched.
   */
  listQueryKey: unknown[] = []
): Promise<Page<T>> {
  const limit = Math.min(request.limit, MAX_PAGE_LIMIT);

  const fetchIt = () =>
    get<T[] | Page<T>>(path, {
      ...filters,
      limit,
      offset: request.offset,
      ...(request.search ? { search: request.search } : {}),
      ...(request.sort ? { sort: request.sort } : {}),
      ...(request.desc ? { desc: true } : {}),
    });

  // Keyed on the endpoint and its NON-paging filters: those decide which rows the whole
  // list contains, while limit/offset/sort/search are exactly what the flag-off server
  // ignored.
  const response = queryClient
    ? await fetchListSharingFallback<T>(
      queryClient,
      [...listQueryKey, WHOLE_LIST_FALLBACK, path, filters],
      fetchIt
    )
    : await fetchIt();

  if (isPage<T>(response)) {
    return { ...response, serverPaged: true };
  }

  // Flag off: the legacy full array, unsearched and unsorted.
  let all = response ?? [];

  if (request.search) {
    if (!fallback.searchable) {
      // Loud rather than plausible. Returning the unfiltered window here is the bug this
      // guard exists for, and it looks exactly like a working search.
      throw new Error(
        `${path}: the server ignored ?search= (ORG_SCALE_PAGINATION off) and this caller ` +
        'supplied no fallback matcher, so the page would be unfiltered rows presented as results'
      );
    }
    all = all.filter((item) => matchesSearch(item, request.search as string, fallback));
  }

  const unfilteredTotal = (response ?? []).length;

  const sortField = request.sort;
  if (sortField) {
    if (!fallback.sortValue) {
      // Same rule as the search above, and for the same reason: a table showing a sort
      // indicator over rows nobody sorted is a confident wrong answer, not a degradation.
      throw new Error(
        `${path}: the server ignored ?sort= (ORG_SCALE_PAGINATION off) and this caller ` +
        'supplied no fallback sortValue, so the page would be unsorted rows under a sort indicator'
      );
    }
    const direction = request.desc ? -1 : 1;
    all = [...all].sort((a, b) => {
      // Ordered the way MySQL orders it, so flipping ORG_SCALE_PAGINATION does not
      // reshuffle a page: the collation these columns use is case-insensitive (so "queen"
      // and "Queen" interleave rather than splitting into two blocks), NULLs sort first
      // ascending, and ties break on the row's id — which is what stops a page walk from
      // repeating one row and skipping another.
      const left = foldForSort(fallback.sortValue!(a, sortField));
      const right = foldForSort(fallback.sortValue!(b, sortField));

      if (left !== right) {
        // NULL/undefined first on ascending, last on descending — MySQL's rule, not
        // "unknowns to the end" regardless of direction.
        if (left === undefined) return -1 * direction;
        if (right === undefined) return 1 * direction;
        return (left < right ? -1 : 1) * direction;
      }

      const leftId = idOf(a);
      const rightId = idOf(b);
      if (leftId === rightId) return 0;
      return leftId < rightId ? -1 : 1;
    });
  }

  return {
    items: all.slice(request.offset, request.offset + limit),
    total: all.length,
    limit,
    offset: request.offset,
    serverPaged: false,
    unfilteredTotal,
  };
}

/**
 * There is deliberately NO "fetch every page" helper here.
 *
 * There was one, and every caller of it was wrong in the same way: it turned one enormous
 * request into a bounded number of merely large ones and left the whole table, and the
 * searching and sorting over it, in the browser. That is not pagination — it is the same
 * payload with more round trips.
 *
 * A screen that shows a page asks for a page ({@link getPage}). A picker that needs to
 * find one row out of thousands sends a search term and shows the matches. A caller that
 * genuinely needs every row of a large table is doing something the API should answer
 * directly — a count, an aggregate, an export — and should get an endpoint for it, as the
 * Overview page and the covid-care dashboard did.
 */
